import { useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { APP_CONFIG } from '../config/app-config';
import { isMarketingStandaloneNavPath } from './marketing/marketingStandaloneNav';

const COPYRIGHT_NAME = import.meta.env.VITE_SITE_COPYRIGHT_NAME || 'TENSION Labs';
const OFFICIAL_X_PROFILE_URL = 'https://x.com/UseTensionAI';
const OFFICIAL_X_FOLLOW_URL = 'https://twitter.com/UseTensionAI?ref_src=twsrc%5Etfw';

function OfficialXFollowButton({ compact = false }: { compact?: boolean }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const scriptId = 'x-widget-script';
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    const twttr = (
      window as Window & { twttr?: { widgets?: { load: (element?: HTMLElement | null) => void } } }
    ).twttr;

    if (!existingScript) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      script.charset = 'utf-8';
      script.onload = () => {
        (
          window as Window & { twttr?: { widgets?: { load: (element?: HTMLElement | null) => void } } }
        ).twttr?.widgets?.load(containerRef.current);
      };
      document.body.appendChild(script);
      return;
    }

    twttr?.widgets?.load(containerRef.current);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`rounded-xl border border-cyan-400/40 bg-slate-900/70 p-3 shadow-[0_0_0_1px_rgba(34,211,238,0.2),0_10px_30px_rgba(15,23,42,0.45)] ${
        compact ? '' : 'min-w-[220px]'
      }`}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cyan-300">Official X</p>
      <a
        href={OFFICIAL_X_FOLLOW_URL}
        className="twitter-follow-button"
        data-size="large"
        data-show-screen-name="false"
        data-show-count="false"
      >
        Follow @UseTensionAI
      </a>
      <a
        href={OFFICIAL_X_PROFILE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-flex text-xs text-slate-300 underline-offset-2 hover:text-cyan-300 hover:underline"
      >
        @UseTensionAI
      </a>
    </div>
  );
}

/** Footer for TENSION for Work / enterprise marketing (password-gated enterprise routes). */
function EnterpriseMarketingFooter({ year }: { year: number }) {
  const enterpriseSocialLinks = [
    { label: 'X', href: APP_CONFIG.socialProfiles.x || OFFICIAL_X_PROFILE_URL, icon: Twitter, aria: 'TENSION on X' },
    { label: 'LinkedIn', href: APP_CONFIG.socialProfiles.linkedin, icon: Linkedin, aria: 'TENSION on LinkedIn' },
  ].filter((item) => item.href);

  const sections: Array<{ title: string; links: Array<{ to: string; label: string }> }> = [
    {
      title: 'Platform',
      links: [
        { to: '/enterprise', label: 'Overview' },
        { to: '/enterprise/platform', label: 'Architecture' },
        { to: '/enterprise/solutions', label: 'Solutions' },
        { to: '/enterprise/expansion', label: 'Roadmap & expansion' },
      ],
    },
    {
      title: 'Trust & access',
      links: [
        { to: '/enterprise/security', label: 'Security & compliance' },
        { to: '/enterprise/identity', label: 'Identity & SSO' },
      ],
    },
    {
      title: 'Programs',
      links: [
        { to: '/enterprise/pilot', label: 'Pilot program' },
        { to: '/enterprise/whitelabel', label: 'White-label' },
      ],
    },
    {
      title: 'Partners & procurement',
      links: [
        { to: '/enterprise/partner-docs', label: 'Partner API docs' },
        { to: '/business-inquiries?inquiryType=enterprise', label: 'Enterprise sales' },
        { to: '/support', label: 'Support' },
      ],
    },
  ];

  return (
    <footer
      className="mt-auto border-t border-slate-700/80 bg-slate-950/95 text-slate-200 backdrop-blur-sm"
      role="contentinfo"
    >
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="max-w-md">
            <p className="text-sm font-semibold text-slate-100">TENSION for Work</p>
            <p className="mt-1 text-sm text-slate-400">
              Relationship intelligence for employers and benefits teams — aggregate workforce signals with privacy-aware
              controls built for procurement and security review.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              <Link to="/" className="text-slate-400 underline-offset-2 hover:text-cyan-300 hover:underline">
                Consumer relationship app
              </Link>
              <span className="mx-1.5 text-slate-600">·</span>
              <span>Not intended for clinical diagnosis or therapy.</span>
            </p>
          </div>
          {enterpriseSocialLinks.length > 0 ? (
            <div>
              <p className="text-sm font-medium text-slate-200">Company</p>
              <div className="mt-2 mb-3">
                <OfficialXFollowButton compact />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {enterpriseSocialLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600/80 bg-slate-900/60 px-3 py-2 text-sm text-slate-200 transition-colors hover:border-cyan-500/50 hover:bg-slate-800/80"
                      aria-label={item.aria}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </a>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="text-sm font-semibold text-slate-100">{section.title}</h4>
              <div className="mt-3 space-y-2">
                {section.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-sm text-slate-400 transition-colors hover:text-cyan-300"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-slate-800 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {COPYRIGHT_NAME}. All rights reserved.
            <span className="mx-2">·</span>
            <span className="font-medium text-slate-400">Patent Pending</span>
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-cyan-400">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-cyan-400">
              Terms
            </Link>
            <Link to="/about" className="hover:text-cyan-400">
              About
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Site-wide footer: share actions (native intents — no tracking pixels) + copyright.
 * Admin/debug routes get a minimal copyright-only bar.
 */
export function SiteFooter() {
  const location = useLocation();
  const year = new Date().getFullYear();

  const path = location.pathname;
  const minimalFooter =
    path.startsWith('/admin') ||
    path.startsWith('/marketing') ||
    path.startsWith('/debug') ||
    path.startsWith('/error-logs');

  // Pages with custom full marketing footers should not render the shared footer.
  if (isMarketingStandaloneNavPath(path)) {
    return null;
  }

  if (path.startsWith('/enterprise')) {
    return <EnterpriseMarketingFooter year={year} />;
  }

  if (minimalFooter) {
    return (
      <footer
        className="border-t border-[rgba(255,255,255,0.05)] bg-[rgba(17,24,39,0.6)] backdrop-blur-[20px]"
        role="contentinfo"
      >
        <div className="mx-auto max-w-[1200px] px-6 py-3 text-center text-xs text-[#64748B]">
          © {year} {COPYRIGHT_NAME}. All rights reserved.
          <span className="mx-2">•</span>
          <span className="font-medium">Patent Pending</span>
          <span className="mx-2">•</span>
          <Link to="/contact" className="hover:text-[#22D3EE]">
            Contact
          </Link>
        </div>
      </footer>
    );
  }

  const socialLinks = [
    { label: 'X', href: APP_CONFIG.socialProfiles.x || OFFICIAL_X_PROFILE_URL, icon: Twitter, aria: 'Visit us on X' },
    { label: 'Facebook', href: APP_CONFIG.socialProfiles.facebook, icon: Facebook, aria: 'Visit us on Facebook' },
    { label: 'LinkedIn', href: APP_CONFIG.socialProfiles.linkedin, icon: Linkedin, aria: 'Visit us on LinkedIn' },
    { label: 'Instagram', href: APP_CONFIG.socialProfiles.instagram, icon: Instagram, aria: 'Visit us on Instagram' },
  ].filter((item) => item.href);
  const footerSections: Array<{ title: string; links: Array<{ to: string; label: string }> }> = [
    {
      title: 'Product',
      links: [
        { to: '/pricing', label: 'Pricing' },
        { to: '/home#relationship-intelligence', label: 'Relationship Intelligence' },
        { to: '/relationship-intelligence/faq', label: 'Relationship Intelligence FAQ' },
      ],
    },
    {
      title: 'Content',
      links: [
        { to: '/press', label: 'Press' },
        { to: '/best-relationship-app', label: 'Best Relationship App' },
        { to: '/best-couples-app', label: 'Best Couples App' },
      ],
    },
    {
      title: 'Features',
      links: [
        { to: '/features/relationship-intelligence', label: 'Intelligence Suite Page' },
        { to: '/features/conversation-micro-coach', label: 'Micro-Coach Page' },
        { to: '/compare/tension-vs-paired', label: 'TENSION vs Paired' },
        { to: '/alternatives/paired', label: 'Paired Alternatives' },
      ],
    },
    {
      title: 'Business & Support',
      links: [
        { to: '/business-inquiries', label: 'Investors & Business Inquiries' },
        { to: '/support', label: 'Support' },
        { to: '/faq', label: 'FAQ' },
      ],
    },
  ];

  return (
    <footer
      className="mt-auto border-t border-[rgba(255,255,255,0.05)] bg-[rgba(17,24,39,0.6)] shadow-[0_20px_40px_rgba(0,0,0,0.4)] backdrop-blur-[20px]"
      role="contentinfo"
    >
      <div className="mx-auto max-w-[1200px] px-6 py-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium text-white">Follow</p>
            <p className="mt-1 max-w-sm text-sm text-[#94A3B8]">
              Follow official TENSION profiles for product updates and relationship insights.
            </p>
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <OfficialXFollowButton />
            {socialLinks.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-[14px] border border-[rgba(255,255,255,0.05)] px-3 py-2 text-sm text-[#94A3B8] transition-all duration-base ease-out hover:bg-white/[0.04] hover:text-white"
                  aria-label={item.aria}
                >
                  <Icon className="h-4 w-4 stroke-[1.75]" />
                  {item.label}
                </a>
              );
            })}
          </div>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {footerSections.map((section) => (
            <div key={section.title}>
              <h4 className="text-sm font-semibold text-white">{section.title}</h4>
              <div className="mt-3 space-y-2">
                {section.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-sm text-[#94A3B8] transition-colors duration-base ease-out hover:text-[#22D3EE]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-[rgba(255,255,255,0.05)] pt-6 text-xs text-[#64748B] sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {COPYRIGHT_NAME}. All rights reserved.
            <span className="mx-2">•</span>
            <span className="font-medium">Patent Pending</span>
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-[#22D3EE]">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-[#22D3EE]">
              Terms
            </Link>
            <Link to="/about" className="hover:text-[#22D3EE]">
              About
            </Link>
            <Link to="/contact" className="hover:text-[#22D3EE]">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
